import { atom, selectorFamily } from 'recoil';

import {
  MarketRateDictionaryModel,
  MarketRateModel,
  SpotRateDictionaryModel,
  SpotRateModel,
} from '@/lib/models/rates/types';
import { SearchingState } from '@/types';
import { stringsHavePartialMatch } from '@/utils/helpers/strings-have-partial-match';

// Spot Rates
// ---------------------------

export const spotRateResultsState = atom<SpotRateDictionaryModel>({
  key: 'spotRateResultsState',
  default: undefined,
});

export const getSpotRatesByCarrierIdsState = selectorFamily<SpotRateModel[], string[]>({
  key: 'getSpotRatesByCarrierIdsState',
  get:
    (carrierIds: string[] = []) =>
    ({ get }) => {
      const rateDictionary = get(spotRateResultsState);
      if (rateDictionary) {
        return carrierIds
          .map((carrierId) => {
            const rate = rateDictionary[carrierId];
            return rate;
          })
          .filter(Boolean);
      }
      return [];
    },
});

export const getSpotRatesByRouteState = selectorFamily<SpotRateModel[], { pol: string; pod: string }>({
  key: 'getSpotRatesByRouteState',
  get:
    ({ pol, pod }) =>
    ({ get }) => {
      const rateDictionary = get(spotRateResultsState);
      if (rateDictionary) {
        const rates = Object.values(rateDictionary);
        // Ensure we only return matching results for that route
        return rates.filter((rate) => rate.fromId === pol && rate.toId === pod);
      }
      return [];
    },
});

export const spotRateSearchState = atom<SearchingState>({
  key: 'spotRateSearchState',
  default: {
    isSearching: false,
    hasSearched: false,
    error: undefined,
  },
});

// Market Rates
// ---------------------------

export const marketRateResultsState = atom<MarketRateDictionaryModel>({
  key: 'marketRateResultsState',
  default: undefined,
});

export const getMarketRatesByRouteState = selectorFamily<MarketRateModel | undefined, { pol?: string; pod?: string }>({
  key: 'getMarketRatesByRouteState',
  get:
    ({ pol, pod }) =>
    ({ get }) => {
      const rateDictionary = get(marketRateResultsState);
      if (rateDictionary && pol && pod) {
        return Object.values(rateDictionary).find(
          (rate) => stringsHavePartialMatch(pol, rate.pol) && stringsHavePartialMatch(pod, rate.pod),
        );
      }
      return undefined;
    },
});

export const marketRateSearchingState = atom<Record<string, SearchingState>>({
  key: 'marketRateSearchingState',
  default: {},
});

export const getMarketRatesSearchingState = selectorFamily<SearchingState | undefined, { key: string }>({
  key: 'getMarketRatesByRouteState',
  get:
    ({ key }) =>
    ({ get }) => {
      const searchStates = get(marketRateSearchingState);
      if (searchStates && key) {
        return searchStates[key];
      }
      return undefined;
    },
});
